import type { DateInput } from '#shared/format/date'
import { DateHelper } from '#shared/format/date'
import { formatBillingDate } from '~/utils/billingSettingsView'

/**
 * Dates for display, in the user's app locale — the ONE date formatter on the client (MAR-948).
 * Never `toLocaleDateString()`: its output depends on the runtime's own locale and zone, so an SSR
 * page renders one string on the server and another in the browser (a hydration mismatch).
 * `tests/unit/formatHelpersSweep.test.ts` fails if a local copy comes back.
 */
export function useDateHelper() {
  const { locale } = useI18n()

  return {
    formatDateForDisplay: (date: DateInput, dateStyle?: 'short' | 'medium' | 'long' | 'full') =>
      DateHelper.formatDateForDisplay(date, locale.value, dateStyle),
    formatDateTimeForDisplay: (date: DateInput) => DateHelper.formatDateTimeForDisplay(date, locale.value),
    formatTimeForDisplay: (date: DateInput) => DateHelper.formatTimeForDisplay(date, locale.value),
    /** A layout the presets do not cover — a bare month name, `dd/mm/yyyy`, a compact date-time. */
    formatDateWithOptions: (date: DateInput, options: Intl.DateTimeFormatOptions) =>
      DateHelper.formatWithOptions(date, locale.value, options),
    /**
     * A date billing owns (`billing_date`, `paid_through`, `effective_at`, a voucher's `expires_at`): billing's
     * UTC day, not the viewer's local day of that instant (00:00 UTC on 15 Nov is 14 Nov in El Salvador, MAR-1391).
     */
    formatBillingDate: (iso: string | null | undefined) => formatBillingDate(iso, locale.value),
  }
}
